import React, { Suspense, useEffect, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';
import AnnouncementsComponent from 'components/announcements/AnnouncementsComponent';
import { TileInfo, TopicsPopupProps, CertificationLevelType } from 'app/home/index.functions';
import styles from './RightPanel.styles';
import FilterPanel from 'app/home/FilterPanel';
import TilePanel from 'app/home/tile/TilePanel';
import { Tile4FavoriteData } from 'app/home/index';
import { useHomeContext } from 'app/home/context';
import { Box, IconButton, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import HiddenItems from 'app/home/HiddenItems';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import HierarchyPanel from 'app/home/hierarchy/HierarchyPanel';
import { useDefaultCatalogId } from 'hooks/useCatalog';

export default function RightPanel({
    tiles,
    setTile4Favorite,
    customFieldsDisplayOnTileInfo,
}: {
    tiles: TileInfo[];
    setTile4Favorite: (data: Tile4FavoriteData | null) => void;
    customFieldsDisplayOnTileInfo: { [key: string]: string };
}) {
    const { t } = useBundleTranslation(['app/home']);
    const ctx = useHomeContext();
    const [urlSearchParams] = useSearchParams();
    const [homePageState, setHomePageState, settings, currentCatalogId] = [
        ctx.homePageState,
        ctx.setHomePageState,
        ctx.homePageSettings,
        ctx.currentCatalogId,
    ];
    const brandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties);
    const [isAnnouncementExpanded, setIsAnnouncementExpanded] = useState(false);
    const defaultCatalogId = useDefaultCatalogId();
    const announcementCatalogId = currentCatalogId > 0 ? currentCatalogId : defaultCatalogId;

    useEffect(() => {
        //recalc tiles block size
        if (!isAnnouncementExpanded) window.dispatchEvent(new Event('resize'));
    }, [isAnnouncementExpanded]);

    const showFilterPanel = urlSearchParams.get('hide_filter_panel') != 'Y';
    const showAnnouncements = urlSearchParams.get('hide_announcements') != 'Y';
    const showHierarchy = urlSearchParams.get('hide_hierarchy') != 'Y';

    const memorizedFilterPanel = useMemo(
        () => (
            <Stack direction={'row'} sx={styles.filterPanelHolder}>
                <Stack direction={'row'} sx={styles.logoAndCollapseHolder}>
                    {showHierarchy && (
                        <Stack direction={'row'} sx={styles.hierarchyBtnHolder}>
                            <IconButton
                                onClick={() =>
                                    setHomePageState({
                                        ...homePageState,
                                        hierarchyPanelOpen: !homePageState.hierarchyPanelOpen,
                                    })
                                }
                                sx={{ color: 'primary.main', '&:hover': { opacity: 0.75 } }}
                            >
                                <IconMi
                                    icon={
                                        brandThemeProperties?.menu?.catalog_left_icon == 'sidebar'
                                            ? 'sidebar-menu'
                                            : 'drag-and-drop-grid'
                                    }
                                    fontSize="16"
                                />
                            </IconButton>
                        </Stack>
                    )}
                    {ctx.logoImageUrl && (
                        <Box sx={styles.logoHolder}>
                            <Box component={'img'} sx={styles.logoImg} src={ctx.logoImageUrl} alt="" />
                        </Box>
                    )}
                </Stack>
                {showFilterPanel && <FilterPanel key={'filterPanel'} />}
            </Stack>
        ),
        [showFilterPanel, showHierarchy, homePageState.hierarchyPanelOpen, ctx.logoImageUrl],
    );

    const memorizedAnnouncementPanel = useMemo(
        () => (
            <>
                {showAnnouncements && (
                    <Suspense>
                        <Box
                            className={
                                'announcement-wrapper-el ' +
                                (isAnnouncementExpanded ? 'announcement-wrapper--expanded' : '')
                            }
                            sx={styles.announcementHolder}
                            id={'announcementListHome'}
                        >
                            <AnnouncementsComponent
                                catalogId={announcementCatalogId}
                                elementId={0}
                                onChangeCollapsedState={(isExpanded) => {
                                    setIsAnnouncementExpanded(isExpanded);
                                }}
                                placeIntegration={'home'}
                            />
                        </Box>
                    </Suspense>
                )}
            </>
        ),
        [showAnnouncements, isAnnouncementExpanded, announcementCatalogId],
    );

    return (
        <Stack
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {(showFilterPanel || showHierarchy) && <Suspense>{memorizedFilterPanel}</Suspense>}
            <Stack flexGrow={1} direction={'row'} sx={{ overflow: 'hidden' }}>
                {showHierarchy && (
                    <Suspense>
                        <HierarchyPanel />
                    </Suspense>
                )}
                <Stack
                    id={'rightPanel'}
                    className={'rightPanel'}
                    sx={{
                        ...styles.rightPanel,
                        backgroundColor: brandThemeProperties.brand_theme_catalog_background_color,
                        backgroundImage: brandThemeProperties?.images?.brand_theme_catalog_background_image?.file
                            ? `url('/data/content/theme-image/brand_theme_catalog_background_image')`
                            : undefined,
                        backgroundRepeat:
                            brandThemeProperties.brand_theme_catalog_background_layout == 'repeat'
                                ? 'repeat'
                                : 'no-repeat',
                        backgroundPositionX:
                            brandThemeProperties.brand_theme_catalog_background_layout == 'original'
                                ? brandThemeProperties.brand_theme_catalog_background_position
                                : undefined,
                        backgroundSize:
                            brandThemeProperties.brand_theme_catalog_background_layout == 'auto' ? 'cover' : undefined,
                    }}
                >
                    <Suspense>
                        {'Y' == settings?.parameters?.SHOW_NON_VISIBLE_ITEM_COUNT_IN_CATALOG && <HiddenItems />}
                    </Suspense>
                    {memorizedAnnouncementPanel}
                    <Suspense>
                        <Box
                            sx={{
                                overflow: 'hidden',
                                ...(isAnnouncementExpanded ? { height: '0' } : { flexGrow: 1 }),
                            }}
                        >
                            <TilePanel
                                key={'tilePanel'}
                                tiles={tiles}
                                setTile4Favorite={setTile4Favorite}
                                customFieldsDisplayOnTileInfo={customFieldsDisplayOnTileInfo}
                            />
                        </Box>
                    </Suspense>
                </Stack>
            </Stack>
        </Stack>
    );
}
